// src/BankingSolutions.js (New Component - Create this file in src/)
import React from 'react';
import { motion } from 'framer-motion';

const Banking = ({ onBack }) => {
  return (
    <div className="bg-white min-h-screen text-black font-sans">
      {/* Navigation with Back Button */}
      <nav className="sticky top-0 z-50 bg-white shadow-md px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex justify-between items-center">
          <motion.button
            onClick={onBack}
            className="flex items-center text-red-500 hover:text-red-600 text-lg font-semibold transition-colors"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            ← Back to Home
          </motion.button>
          <h1 className="text-2xl font-bold">
            <span className="text-red-500">Brain</span>Serve
          </h1>
        </div>
      </nav>

      {/* Hero Section for Banking */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <motion.h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Banking 
            <span className="text-red-500 block">Solutions</span>
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            At Brain Serve, we make financing your dreams simple and secure. Our comprehensive loan services are tailored to your needs, 
            with competitive rates, flexible terms, and expert guidance every step of the way.
          </motion.p>
          <button className="bg-red-500 hover:bg-red-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-red-500/50">
            Apply Now
          </button>
        </div>
      </section>

      {/* Loan Types Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.h2 
            className="text-4xl font-bold text-center mb-16 text-black"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Our Loan Offerings
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Home Loans */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">🏠</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Home Loans</h3>
              <p className="text-gray-600 mb-4 text-center">Turn your dream home into reality with low-interest home loans up to ₹5 Crores. Flexible EMIs and quick approvals.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Eligibility: Salaried/Self-employed, age 21-65</li>
                <li>• Tenure: Up to 30 years</li>
                <li>• Benefits: Tax deductions, no prepayment charges</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Get Quote
              </button>
            </motion.div>

            {/* Educational Loans */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">🎓</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Educational Loans</h3>
              <p className="text-gray-600 mb-4 text-center">Fund your higher education abroad or in India with loans covering tuition, travel, and living expenses up to ₹1.5 Crores.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Eligibility: Students aged 18+, admission letter required</li>
                <li>• Tenure: Up to 15 years (with moratorium)</li>
                <li>• Benefits: 100% financing, no collateral for up to ₹7.5 Lakhs</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Get Quote
              </button>
            </motion.div>

            {/* Car Loans */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">🚗</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Car Loans</h3>
              <p className="text-gray-600 mb-4 text-center">Drive away in your new or used car with instant approvals and rates starting at 8.5%. Up to 100% on-road price financing.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Eligibility: Stable income, CIBIL score 700+</li>
                <li>• Tenure: Up to 7 years</li>
                <li>• Benefits: Quick disbursal, insurance tie-ups</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Get Quote
              </button>
            </motion.div>

            {/* Business Loans */}
            <motion.div
              className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 shadow-sm hover:shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">💼</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-center">Business Loans</h3>
              <p className="text-gray-600 mb-4 text-center">Scale your enterprise with unsecured loans up to ₹2 Crores for working capital, expansion, or equipment purchase.</p>
              <ul className="text-sm text-gray-500 space-y-1 mb-6">
                <li>• Eligibility: Established business, 2+ years operations</li>
                <li>• Tenure: Up to 5 years</li>
                <li>• Benefits: Minimal documentation, flexible repayment</li>
              </ul>
              <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl transition duration-300">
                Get Quote
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Banking */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <motion.h2 
            className="text-4xl font-bold mb-8 text-black"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Why Brain Serve for Your Loans?
          </motion.h2>
          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Expert Guidance</h3>
              <p className="text-gray-600">Personalized advice from certified financial experts to match the perfect loan for you.</p>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Competitive Rates</h3>
              <p className="text-gray-600">Access to best-in-market interest rates with no hidden fees.</p>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-red-500">Seamless Process</h3>
              <p className="text-gray-600">Digital application, fast approvals, and door-step service for hassle-free experience.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 text-center text-gray-500 border-t border-gray-200">
        <p>&copy; 2025 Brain Serve. All rights reserved. | Secure Your Future Today.</p>
      </footer>
    </div>
  );
};

export default Banking;